import { useUserIdentity } from '@src/stores-v2';
import EditSparkleIcon from '@src/svgs/EditSparkleIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { SELECTION_COLORS } from '../constants/selectionColors';
import { useVideoPlayer } from '../hooks/VideoPlayerContext';
import { SpeakerEvent } from '../services/interfaces/ISpeakerService';
import { TranscriptEditorChangeType } from '../types';
import SpeakerFormModal from './modals/SpeakerFormModal';
import SpeakerIdentificationModal from './modals/SpeakerIdentificationModal';
import SpeakerContextMenu from './SpeakerContextMenu';

import type { TranscriptEditorStore } from '../TranscriptEditorStore';

// Props interface
interface SpeakersPanelProps {
  editorStore: TranscriptEditorStore;
}

// Speaker type
interface Speaker {
  speakerLabel: string;
  datoSpeakerId: string;
  name: string;
  title?: string;
  voice?: string;
  bio?: string;
  color: string;
}

/**
 * SpeakersPanel component displays a list of speakers with tabs for Story and Organization
 * speakers, along with their colors and titles.
 */
const SpeakersPanel = observer(({ editorStore }: SpeakersPanelProps) => {
  const [activeTab, setActiveTab] = useState<
    'story' | 'organization' | 'selections'
  >('story');
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [activeButtonRef, setActiveButtonRef] =
    useState<React.RefObject<HTMLElement> | null>(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [showIdentificationModal, setShowIdentificationModal] = useState(false);
  const speakerService = editorStore.editor?.getSpeakerService();
  const userIdentity = useUserIdentity();
  const { enableSpeakersIdentification } = useFlagsCombination(
    userIdentity.currentRole,
  );

  // Get video player API for seeking
  const videoPlayerAPI = useVideoPlayer();

  // Add a force refresh counter to update speaker list when needed
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Set up a listener for speaker service events
  useEffect(() => {
    if (!speakerService) return;

    // Listen for events that should cause panel refresh
    const addedSubscription = speakerService.on(
      SpeakerEvent.SPEAKER_ADDED,
      () => {
        // console.log('Speaker added, refreshing panel');
        setRefreshCounter(prev => prev + 1);
      },
    );

    // Listen for mapping updates which happen when speakers are assigned
    const mappingsSubscription = speakerService.on(
      SpeakerEvent.MAPPINGS_UPDATED,
      () => {
        // console.log('Speaker mappings updated, refreshing panel');
        // Force a refresh of the UI with a short delay to ensure the mappings are fully processed
        setTimeout(() => {
          setRefreshCounter(prev => prev + 1);
        }, 100);
      },
    );

    // Listen for speaker assignments which happen when speakers are assigned to selections
    const speakerAssignmentSubscription = speakerService.on(
      SpeakerEvent.SPEAKER_ASSIGNED,
      () => {
        // console.log('Speaker assigned, refreshing panel');
        setRefreshCounter(prev => prev + 1);
      },
    );

    // Also listen for the speakers loaded event to refresh the panel
    const speakersLoadedSubscription = speakerService.on(
      SpeakerEvent.SPEAKERS_LOADED,
      () => {
        // console.log('Speakers loaded, refreshing panel');
        setRefreshCounter(prev => prev + 1);
      },
    );

    // Listen for speaker updates (when properties like title are changed)
    const speakerUpdatedSubscription = speakerService.on(
      SpeakerEvent.SPEAKER_UPDATED,
      () => {
        // console.log('Speaker updated, refreshing panel');
        setRefreshCounter(prev => prev + 1);
      },
    );

    return () => {
      // Clean up subscriptions
      addedSubscription.unsubscribe();
      mappingsSubscription.unsubscribe();
      speakerAssignmentSubscription.unsubscribe();
      speakersLoadedSubscription.unsubscribe();
      speakerUpdatedSubscription.unsubscribe();
    };
  }, [speakerService, editorStore.editor]);

  // Get speakers from the editor store based on active tab
  const speakerCategories = useMemo(() => {
    if (!speakerService)
      return {
        assigned: [],
        anonymous: [],
        unassigned: [],
        organization: [],
      };

    const allSpeakers = speakerService.getAllSpeakers(
      editorStore.editor?.getContainer(),
      editorStore,
    );
    return {
      // Mapped speakers that have Dato speaker ID (Assigned)
      assigned: allSpeakers.mapped,
      // Unmapped speakers (Anonymous)
      anonymous: allSpeakers.unmapped,
      // Story speakers not used in transcript (Unassigned)
      unassigned: allSpeakers.story,
      // Organization speakers
      organization: allSpeakers.album,
    };
  }, [
    speakerService?.speakerMappings,
    activeTab,
    editorStore.editor,
    editorStore.transcriptionLoaded,
    editorStore.container,
    refreshCounter,
  ]);

  // Get color data for selections tab
  const colorData = useMemo(() => {
    const usedColors = editorStore.getUsedColors();

    return SELECTION_COLORS.map(color => {
      const elements =
        editorStore.editor?.getElementsWithColor(color.value) || [];
      return {
        ...color,
        count: elements.length,
        isUsed: usedColors.includes(color.value),
      };
    }).filter(color => color.isUsed); // Only show used colors
  }, [editorStore, editorStore.container?.counter]); // Re-compute when container changes

  // Handler for adding a new speaker
  const handleAddSpeaker = () => {
    setSelectedSpeaker(null); // Clear any selected speaker

    setShowSpeakerModal(true);
  };

  // Handler for speaker overflow button click
  const handleOverflowButtonClick = (e: React.MouseEvent, speaker: Speaker) => {
    e.stopPropagation(); // Prevent triggering the speaker row click

    // Instead of manually setting .current, use a mutable object
    // that contains the element for positioning
    const buttonPositionRef = {
      current: e.currentTarget as HTMLElement,
    };

    // Set the position ref, selected speaker, and open the context menu
    setActiveButtonRef(buttonPositionRef as React.RefObject<HTMLElement>);
    setSelectedSpeaker(speaker);
    setContextMenuOpen(true);
  };

  // Handler for closing the context menu
  const handleCloseContextMenu = () => {
    setContextMenuOpen(false);
  };

  // Handler for selecting edit from the context menu
  const handleEditSpeaker = () => {
    setContextMenuOpen(false);
    setShowSpeakerModal(true);
    // selectedSpeaker is already set from handleOverflowButtonClick
  };

  // Handler for adding a speaker to the story (for org speakers)
  const handleAddToStory = async () => {
    if (
      !selectedSpeaker ||
      !selectedSpeaker.datoSpeakerId ||
      !editorStore.storyId
    ) {
      return;
    }

    try {
      const speakerService = editorStore.editor?.getSpeakerService();
      if (!speakerService) {
        console.error('Speaker service not available');
        return;
      }

      // Use the speaker repository to add the speaker to the story
      const rootStore = editorStore.getRootStore();
      if (!rootStore || !rootStore.datoClientStore) {
        console.error('Root store or DatoClientStore not available');
        return;
      }

      const speakerRepo = rootStore.datoClientStore.speakerRepository;
      if (!speakerRepo) {
        console.error('Speaker repository not available');
        return;
      }

      // Add the speaker reference to the story
      await speakerRepo.addSpeakerReferenceToStory(
        selectedSpeaker.datoSpeakerId,
        editorStore.storyId,
      );

      // update the storySpeakers array in the editor store
      editorStore.addStorySpeaker({
        id: selectedSpeaker.datoSpeakerId,
        name: selectedSpeaker.name,
        title: selectedSpeaker.title,
        color: selectedSpeaker.color,
        voice: selectedSpeaker.voice,
        bio: selectedSpeaker.bio,
      });

      // Refresh the speakers list
      setRefreshCounter(prev => prev + 1);

      // Switch to the story tab to show the newly added speaker
      setActiveTab('story');
    } catch (error) {
      console.error('Error adding speaker to story:', error);
    }
  };

  // Handler for removing a speaker from the story (keeps in organization)
  const handleRemoveFromStory = async () => {
    if (
      !selectedSpeaker ||
      !selectedSpeaker.datoSpeakerId ||
      !editorStore.storyId
    ) {
      return;
    }

    try {
      const rootStore = editorStore.getRootStore();
      if (!rootStore || !rootStore.datoClientStore) {
        console.error('Root store or DatoClientStore not available');
        return;
      }

      const speakerRepo = rootStore.datoClientStore.speakerRepository;
      if (!speakerRepo) {
        console.error('Speaker repository not available');
        return;
      }

      // Remove the speaker reference from the story
      await speakerRepo.removeSpeakerReferenceFromStory(
        selectedSpeaker.datoSpeakerId,
        editorStore.storyId,
      );

      // remove the speaker from the storySpeakers array in the editor store
      editorStore.removeStorySpeaker(selectedSpeaker.datoSpeakerId);

      // Refresh the speakers list
      setRefreshCounter(prev => prev + 1);
    } catch (error) {
      console.error('Error removing speaker from story:', error);
    }
  };

  // Handler for unassigning a speaker (removes from voice mappings but keeps in story)
  const handleUnassignSpeaker = async () => {
    if (
      !selectedSpeaker ||
      !selectedSpeaker.speakerLabel ||
      !editorStore.storyId
    ) {
      return;
    }

    try {
      const speakerService = editorStore.editor?.getSpeakerService();
      if (!speakerService) {
        console.error('Speaker service not available');
        return;
      }

      // Clear the speaker mapping (this removes the voice mapping association)
      await editorStore.editor.updateSpeakerMapping(
        selectedSpeaker.speakerLabel,
        '',
      );

      // Notify about the update
      editorStore.editor.notifyObservers(
        TranscriptEditorChangeType.SPEAKER_ASSIGNED,
        {
          speakerLabel: selectedSpeaker.speakerLabel,
          datoSpeakerId: null,
        },
      );

      // Refresh the speakers list
      setRefreshCounter(prev => prev + 1);
    } catch (error) {
      console.error('Error unassigning speaker:', error);
    }
  };

  // Function to seek to a specific timestamp
  const jumpToTimestamp = useCallback(
    (time: number) => {
      // Update video player time
      videoPlayerAPI.seekTo(time);
    },
    [videoPlayerAPI],
  );

  // Handler for scrolling to the next speaker segment after current playhead
  const handleSpeakerClick = useCallback(
    (speakerLabel: string) => {
      if (!speakerLabel) return; // Skip for speakers without a label
      const container = editorStore.editor?.getContainer();
      if (!container) return;

      // Get current playhead position from video player
      const currentTime = videoPlayerAPI.getCurrentTime();

      // Find all speaker segments in the transcript
      const allSegments = Array.from(
        document.querySelectorAll(`[data-speaker-label="${speakerLabel}"]`),
      );
      if (allSegments.length === 0) return;

      // Get start time for each segment and sort by time
      const segmentsWithTime = allSegments
        .map(segment => {
          const startTimeAttr = segment.getAttribute('data-start-time');
          const startTime = startTimeAttr ? parseFloat(startTimeAttr) : 0;
          return { element: segment, startTime };
        })
        .sort((a, b) => a.startTime - b.startTime);

      // Find the next segment after the current playhead
      const nextSegment = segmentsWithTime.find(
        seg => seg.startTime > currentTime,
      );

      // Get target segment (next segment or first segment if looping)
      const targetSegment = nextSegment || segmentsWithTime[0];

      setTimeout(() => {
        const transcriptContainer = document.querySelector(
          '.transcript-scroll-container',
        );

        if (targetSegment.element && transcriptContainer) {
          // Get the positions of the container and segment
          const containerRect = transcriptContainer.getBoundingClientRect();
          const segmentRect = targetSegment.element.getBoundingClientRect();

          // Scroll to make the segment visible in the container
          transcriptContainer.scrollTop =
            (targetSegment.element as HTMLElement).offsetTop -
            (transcriptContainer as HTMLElement).offsetTop -
            containerRect.height / 2 +
            segmentRect.height / 2;

          // Add highlight effect
          targetSegment.element.classList.add('highlight-segment');
          setTimeout(() => {
            targetSegment.element.classList.remove('highlight-segment');
          }, 2000);

          // Seek to the segment's start time
          jumpToTimestamp(targetSegment.startTime);
        }
      }, 0);
    },
    [editorStore.editor, jumpToTimestamp, videoPlayerAPI],
  );

  // Handle clicking on a color to scroll to next instance
  const handleColorClick = useCallback(
    (colorValue: string) => {
      const container = editorStore.editor?.getContainer();
      if (!container) return;

      // Get current playhead position
      const currentTime = videoPlayerAPI.getCurrentTime();

      // Find all elements with this color
      const allElements = Array.from(
        document.querySelectorAll(`[data-element-color="${colorValue}"]`),
      );
      if (allElements.length === 0) return;

      // Get start time for each element and sort by time
      const elementsWithTime = allElements
        .map(element => {
          const startTimeAttr = element.getAttribute('data-start-time');
          const startTime = startTimeAttr ? parseFloat(startTimeAttr) : 0;
          return { element, startTime };
        })
        .sort((a, b) => a.startTime - b.startTime);

      // Find the next element after current playhead
      const nextElement = elementsWithTime.find(
        el => el.startTime > currentTime,
      );

      // Get target element (next element or first element if looping)
      const targetElement = nextElement || elementsWithTime[0];

      setTimeout(() => {
        const transcriptContainer = document.querySelector(
          '.transcript-scroll-container',
        );

        if (targetElement.element && transcriptContainer) {
          // Get positions
          const containerRect = transcriptContainer.getBoundingClientRect();
          const elementRect = targetElement.element.getBoundingClientRect();

          // Calculate scroll position to center the element
          const scrollTop =
            (targetElement.element as HTMLElement).offsetTop -
            (transcriptContainer as HTMLElement).offsetTop -
            containerRect.height / 2 +
            elementRect.height / 2;

          // Scroll to the element
          transcriptContainer.scrollTop = scrollTop;

          // Add highlight effect
          targetElement.element.classList.add('highlight-element');
          setTimeout(() => {
            targetElement.element.classList.remove('highlight-element');
          }, 2000);

          // Seek to the element's start time
          jumpToTimestamp(targetElement.startTime);
        }
      }, 0);
    },
    [editorStore.editor, jumpToTimestamp, videoPlayerAPI],
  );

  // Render a list of speakers with a section title
  const renderSpeakerSection = (title: string, speakers: Speaker[]) => {
    if (speakers.length === 0) return null;

    return (
      <SpeakerSection>
        <SectionHeaderContainer>
          <SectionHeader>{title}</SectionHeader>
        </SectionHeaderContainer>
        {speakers.map((speaker, index) => {
          const speakerColor =
            speakerService?.getSpeakerColor(speaker.speakerLabel) || '#A9A9A9';
          return (
            <SpeakerRow
              key={speaker.speakerLabel || speaker.datoSpeakerId || index}
              onClick={() => handleSpeakerClick(speaker.speakerLabel)}
              title={
                speaker.speakerLabel
                  ? 'Click to find speaker in transcript'
                  : ''
              }
              $clickable={!!speaker.speakerLabel}
            >
              <SpeakerColorIndicator color={speakerColor} />
              <SpeakerInfo>
                <SpeakerName color={speakerColor}>
                  {speaker.name ||
                    `Speaker ${speaker.speakerLabel?.replace('spk_', '')}`}
                </SpeakerName>
                {speaker.title && <SpeakerTitle>{speaker.title}</SpeakerTitle>}
              </SpeakerInfo>
              <SpeakerActions>
                {/* For both anonymous and named speakers, show the edit button but with different tooltips and behavior */}
                <OverflowButton
                  onClick={e => handleOverflowButtonClick(e, speaker)}
                  title={
                    speaker.datoSpeakerId
                      ? 'Speaker options'
                      : 'Replace anonymous speaker with a new speaker'
                  }
                >
                  ⋮
                </OverflowButton>
              </SpeakerActions>
            </SpeakerRow>
          );
        })}
      </SpeakerSection>
    );
  };

  // Render the color selections
  const renderColorSection = () => {
    if (colorData.length === 0) {
      return (
        <EmptyState>
          No colored selections yet.
          <br />
          Select text and assign a color to see it here.
        </EmptyState>
      );
    }

    return (
      <SpeakerSection>
        {colorData.map(color => (
          <SpeakerRow
            key={color.value}
            onClick={() => handleColorClick(color.value)}
            title="Click to find next occurrence in transcript"
            $clickable={true}
          >
            <ColorIndicator color={color.value} />
            <SpeakerInfo>
              <SpeakerName color={color.value}>{color.name}</SpeakerName>
              <SpeakerTitle>
                {color.count} occurrence{color.count !== 1 ? 's' : ''}
              </SpeakerTitle>
            </SpeakerInfo>
            <SpeakerActions>
              <OverflowButton disabled title="More options coming soon">
                <EllipsisIcon />
              </OverflowButton>
            </SpeakerActions>
          </SpeakerRow>
        ))}
      </SpeakerSection>
    );
  };

  return (
    <SpeakersPanelContainer>
      <PanelHeader>
        <SectionTitleHeader>
          <SectionTitle>Speakers</SectionTitle>

          {enableSpeakersIdentification &&
            activeTab === 'story' &&
            speakerCategories.anonymous.length > 0 && (
              <HeaderButton onClick={() => setShowIdentificationModal(true)}>
                <EditSparkleIcon width="16" height="16" />
                ID Speakers
              </HeaderButton>
            )}
          {activeTab !== 'selections' && (
            <HeaderButton onClick={handleAddSpeaker}>Add Speaker</HeaderButton>
          )}
        </SectionTitleHeader>
        <TabsAndButtonContainer>
          <TabsContainer>
            <Tab
              isActive={activeTab === 'story'}
              onClick={() => setActiveTab('story')}
            >
              Story
            </Tab>
            <Tab
              isActive={activeTab === 'organization'}
              onClick={() => setActiveTab('organization')}
            >
              Organization
            </Tab>
            <Tab
              isActive={activeTab === 'selections'}
              onClick={() => setActiveTab('selections')}
            >
              Selections
            </Tab>
          </TabsContainer>
        </TabsAndButtonContainer>
      </PanelHeader>

      <SpeakersList>
        {activeTab === 'story' ? (
          <>
            {renderSpeakerSection('Assigned', speakerCategories.assigned)}
            {renderSpeakerSection('Anonymous', speakerCategories.anonymous)}
            {renderSpeakerSection('Unassigned', speakerCategories.unassigned)}
          </>
        ) : activeTab === 'organization' ? (
          renderSpeakerSection(
            'Organization Speakers',
            speakerCategories.organization,
          )
        ) : (
          renderColorSection()
        )}
      </SpeakersList>

      {/* Unified modal for creating or editing speakers */}
      {showSpeakerModal && (
        <SpeakerFormModal
          isOpen={showSpeakerModal}
          onClose={() => {
            setShowSpeakerModal(false);
            // Force a refresh of the speaker list when the modal closes
            setRefreshCounter(prevCount => prevCount + 1);
          }}
          editorStore={editorStore}
          skipAssignment={true} // Skip assignment when created from panel
          speakerToEdit={
            selectedSpeaker
              ? {
                  speakerLabel: selectedSpeaker.speakerLabel,
                  datoSpeakerId: selectedSpeaker.datoSpeakerId,
                  name: selectedSpeaker.name,
                  title: selectedSpeaker.title,
                  bio: selectedSpeaker.bio,
                  voice: selectedSpeaker.voice,
                }
              : undefined
          }
          modalOptions={{
            addToStory: activeTab === 'story',
            addToOrganization: true,
          }}
        />
      )}

      {/* Speaker identification modal */}
      {showIdentificationModal && (
        <SpeakerIdentificationModal
          isOpen={showIdentificationModal}
          onClose={() => setShowIdentificationModal(false)}
          editorStore={editorStore}
          anonymousSpeakers={speakerCategories.anonymous}
        />
      )}

      {/* Context menu for speaker options */}
      <SpeakerContextMenu
        isOpen={contextMenuOpen}
        buttonRef={activeButtonRef}
        onClose={handleCloseContextMenu}
        onEdit={handleEditSpeaker}
        onAddToStory={handleAddToStory}
        onRemoveFromStory={handleRemoveFromStory}
        onUnassign={handleUnassignSpeaker}
        speakerName={selectedSpeaker?.name || 'Speaker'}
        isAnonymous={!selectedSpeaker?.datoSpeakerId}
        showAddToStory={activeTab === 'organization'}
        showRemoveFromStory={
          activeTab === 'story' &&
          speakerCategories.unassigned.some(
            (s: Speaker) => s.datoSpeakerId === selectedSpeaker?.datoSpeakerId,
          )
        }
        showUnassign={
          activeTab === 'story' &&
          speakerCategories.assigned.some(
            (s: Speaker) => s.datoSpeakerId === selectedSpeaker?.datoSpeakerId,
          )
        }
      />
    </SpeakersPanelContainer>
  );
});

// Styled Components
const SpeakersPanelContainer = styled.div`
  grid-area: speakers;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-left: 1px solid #1a1e23;
  overflow: hidden; /* This prevents the container from scrolling */
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 8px;
  border-bottom: 1px solid #1a1e23;
`;

const SectionTitleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto 0 0;
  color: #ffffff;
`;

const TabsAndButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const Tab = styled.button<{ isActive?: boolean }>`
  background: none;
  border: none;
  color: ${props => (props.isActive ? '#f2d093' : '#A0A6AE')};
  padding: 4px 0;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border-bottom: 1px solid
    ${props => (props.isActive ? '#f2d093' : 'transparent')};
`;

const HeaderButton = styled.button`
  box-sizing: border-box;
  display: flex;
  height: 32px;
  padding: 16px;
  background: none;
  outline: none;
  border-radius: 8px;
  border: 1px solid #484848;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  color: #17c964;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    border-color: #17c964;
  }
`;

const SpeakersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  height: 0; /* This forces the element to take available space and enables scrolling */
`;

// New styled component for speaker sections
const SpeakerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Container for section header and auto-identify button
const SectionHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #2a2f36;
  margin-bottom: 4px;
  padding: 4px 0;
`;

// New styled component for section headers
const SectionHeader = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #a0a6ae;
`;

const SpeakerRow = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${props =>
      props.$clickable ? '#1A1E25' : 'transparent'};
  }
`;

const SpeakerColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 12px;
`;

const SpeakerInfo = styled.div`
  flex: 1;
`;

const SpeakerName = styled.div<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.color};
`;

const SpeakerTitle = styled.div`
  font-size: 12px;
  color: #a0a6ae;
`;

const SpeakerActions = styled.div`
  display: flex;
  align-items: center;
`;

const OverflowButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const ColorIndicator = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #a0a6ae;
  font-size: 14px;
  line-height: 1.5;
`;

export default SpeakersPanel;
